import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdditionalSupervisors, setAdditionalSupervisors } from '@/api/onsiteLiveAdmin';
import { ApiError } from '@/api/client';
import type { AdditionalSupervisor } from '@/types/oscar';
import { OnsiteLiveUserPicker } from './OnsiteLiveUserPicker';
import { ErrorBanner } from '../BranchRelationships/shared';
import { btnPrimary, btnSecondary } from '../BranchRelationships/styles';

type Draft = Pick<AdditionalSupervisor, 'SupervisorUserId' | 'Name' | 'Title' | 'Active'>;

/**
 * A user's ADDITIONAL SUPERVISORS (team walk-through 26/10/2026: External manager
 * becomes "Additional supervisor", more than one). Each is an extra "Additional"
 * PO approver in OnFlow, beside the direct manager - OnsiteLive's
 * OL_UserAdditionalManagers, now oscar.OnsiteLiveUserAdditionalManager. Read only
 * until the subscriptions cutover (the API's WritesOpen), then admin edits here.
 */
export function AdditionalSupervisorsEditor({ userId, username, canWrite }: { userId: number; username: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const key = ['onsitelive-admin', 'user', userId, 'additional-supervisors'];
  const q = useQuery({ queryKey: key, queryFn: ({ signal }) => getAdditionalSupervisors(userId, signal), retry: false });
  const [draft, setDraft] = useState<Draft[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setDraft(q.data?.Supervisors ?? []); }, [q.data]);

  const save = useMutation({
    mutationFn: () => setAdditionalSupervisors(userId, draft.map((d) => d.SupervisorUserId)),
    onSuccess: (data) => { setError(null); qc.setQueryData(key, data); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the additional supervisors.'),
  });

  const saved = (q.data?.Supervisors ?? []).map((s) => s.SupervisorUserId).sort((a, b) => a - b).join(',');
  const dirty = draft.map((d) => d.SupervisorUserId).sort((a, b) => a - b).join(',') !== saved;
  const editable = canWrite && q.data?.WritesOpen === true;

  return (
    <section className="rounded border border-border bg-white p-4" aria-label="Additional supervisors">
      <h3 className="font-heading text-base font-bold text-navy">Additional supervisors</h3>
      <p className="mt-1 text-xs text-darkGrey/70">
        Extra people who can approve {username}&apos;s purchase orders in OnFlow, as well as their manager.
      </p>
      {q.isLoading && <p className="mt-2 text-sm text-darkGrey/70">Loading…</p>}
      {q.isError && (
        <p className="mt-2 text-sm text-red">
          {q.error instanceof ApiError ? q.error.message : 'Could not load the additional supervisors.'}
        </p>
      )}
      {q.data && !q.data.WritesOpen && (
        <p role="status" className="mt-2 rounded border border-navy/20 bg-[#E6ECF2] px-3 py-2 text-xs text-darkGrey">
          Read only until the subscriptions cutover: OnsiteLive&apos;s own list is the one OnFlow reads, and this is a copy.
        </p>
      )}
      {q.data && (
        <>
          <ul className="mt-3 flex flex-wrap gap-2" aria-label={`Additional supervisors of ${username}`}>
            {draft.length === 0 && <li className="text-sm text-darkGrey/70">None.</li>}
            {draft.map((d) => (
              <li key={d.SupervisorUserId} className="inline-flex items-center gap-1 rounded bg-[#E6ECF2] px-2 py-1 text-sm text-navy">
                <span>{d.Name ?? `UserId ${d.SupervisorUserId}`}</span>
                {d.Title && <span className="text-xs text-darkGrey/70">· {d.Title}</span>}
                {!d.Active && <span className="text-xs font-semibold text-red">· account disabled</span>}
                {editable && (
                  <button
                    type="button"
                    onClick={() => setDraft(draft.filter((x) => x.SupervisorUserId !== d.SupervisorUserId))}
                    aria-label={`Remove ${d.Name ?? d.SupervisorUserId}`}
                    className="ml-1 rounded px-1 font-semibold text-navy hover:bg-white"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
          {editable && (
            <div className="mt-3 max-w-md">
              <OnsiteLiveUserPicker
                label="Add a supervisor"
                value=""
                onChange={(v, name) => {
                  const idNum = Number(v);
                  if (!v || draft.some((d) => d.SupervisorUserId === idNum)) return;
                  setDraft([...draft, { SupervisorUserId: idNum, Name: name ?? null, Title: null, Active: true }]);
                }}
                canWrite
                excludeId={userId}
              />
            </div>
          )}
          {editable && (
            <div className="mt-3 flex gap-2">
              <button type="button" className={btnPrimary} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save supervisors'}
              </button>
              {dirty && (
                <button type="button" className={btnSecondary} onClick={() => setDraft(q.data?.Supervisors ?? [])}>
                  Undo
                </button>
              )}
            </div>
          )}
          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
          {save.isSuccess && !save.isPending && !dirty && <p role="status" className="mt-2 text-xs text-green">Saved.</p>}
        </>
      )}
    </section>
  );
}
